import { useQuery } from '@tanstack/react-query';
import { getCurrentTenantPublicOptions } from '@/api/query';
import { NotFoundError } from '@/api/errors';

const LEARN41_DOMAINS = ['learn41.com', 'locall41.com', 'devl41.com', 'qal41.com'];

/**
 * Standalone check for learn41 domains. Use this outside of React components
 * where the useTenant() hook is not available (e.g. LoginModal, Sidebar init).
 */
export function isLearn41Domain(): boolean {
  const hostname = window.location.hostname;
  return LEARN41_DOMAINS.some(d => hostname.includes(d.replace('.com', '')));
}

const RECRUIT41_DOMAINS = ['recruit41.com', 'localr41.com', 'devr41.com', 'qar41.com'];

/**
 * Standalone check for recruit41 domains.
 */
export function isRecruit41Domain(): boolean {
  const hostname = window.location.hostname;
  return RECRUIT41_DOMAINS.some(d => hostname.includes(d.replace('.com', '')));
}

function getDomainFromUrl(): { domain: string; baseDomain: string } {
  const hostname = window.location.hostname; // e.g. "tenant.recruit41.com"
  const supportedBaseDomains = ["localr41.com", "locall41.com", "devr41.com", "devl41.com", "qar41.com", "qal41.com", "recruit41.com", "learn41.com"];
  const parts = hostname.split(".");

  let domain;
  let baseDomain;
  // If the hostname has at least three parts, the first part is assumed to be the tenant
  if (parts.length === 3) {
    baseDomain = parts.slice(1).join(".");
    if (supportedBaseDomains.includes(baseDomain)) {
      domain = parts[0];
    } else {
      throw new Error("Invalid base domain");
    }
  } else {
    throw new Error("Invalid hostname, must be in the form of tenant.baseDomain.com");
  }

  return { domain, baseDomain };
}

export function useTenant(): {
  domain: string;
  name: string;
  isDemo: boolean;
  isLearn41: boolean;
  isLocalEnvironment: boolean;
  isDeveloperEnvironment: boolean;
  isQaEnvironment: boolean;
  isProdEnvironment: boolean;
  isLoading: boolean;
  isError: boolean;
  isNotFound: boolean;
} {
  const { domain, baseDomain } = getDomainFromUrl();

  const { data: tenantData, isLoading, isError, error } = useQuery({
    ...getCurrentTenantPublicOptions(),
    retry: false,
  });

  return {
    domain: domain,
    name: isLoading ? "" : tenantData?.name || domain, // Use API name if available, fallback to domain
    isDemo: domain === "demo",
    isLearn41: LEARN41_DOMAINS.includes(baseDomain),
    isLocalEnvironment: (["localr41.com", "locall41.com"].includes(baseDomain)),
    isDeveloperEnvironment: (["devr41.com", "devl41.com", "localr41.com", "locall41.com"].includes(baseDomain)),
    isQaEnvironment: (["qar41.com", "qal41.com"].includes(baseDomain)),
    isProdEnvironment: (["recruit41.com", "learn41.com"].includes(baseDomain)),
    isLoading,
    isError,
    isNotFound: error instanceof NotFoundError,
  };
}
